'use client';

import { useMemo, useContext, useState, type FC } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Flex,
  Heading,
  Icon,
  Pill,
  Separator,
  Text,
} from '@/_components';
import { deleteExtractedDocument } from '@/_api/client-documents';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import styles from './UploadedFile.module.css';
import { ReviewDialog } from '../ReviewDialog';
import { getChildDocuments } from '@/_utils/documents';
import { UploadedFileProps } from './UploadedFile.types';
import { autoSplitDocument } from '@/_api/admin-documents';
import { PillProps } from '@/_components/Pill/Pill.types';

export const UploadedFile: FC<UploadedFileProps> = ({
  documentsRecognized,
  uploadedFile,
  questionnaire,
  reloadQuestionnairePreview,
  isBUPReviewDialogOpenForSource,
  setIsBUPReviewDialogOpenForSource,
}) => {
  const [isRemovingDoc, setIsRemovingDoc] = useState(false);
  const [isRetryLoading, setIsRetryLoading] = useState<boolean>(false);

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const onRemoveDocument = async (documentId: number) => {
    setIsRemovingDoc(true);

    try {
      await deleteExtractedDocument({
        id: documentId,
        token,
      });

      await reloadQuestionnairePreview();
      addToast('success', 'Document removed successfully');
    } catch (error) {
      addToast('error', 'An error occurred while removing the document.', {
        error,
      });
    } finally {
      setIsRemovingDoc(false);
    }
  };

  const wasSuccessful: boolean =
    uploadedFile.status === 'split_succeeded' ||
    uploadedFile.status === 'review_required' ||
    uploadedFile.status === 'identify_succeeded';

  const handleRetry = async () => {
    try {
      setIsRetryLoading(true);

      await autoSplitDocument({
        token,
        extractedDocumentId: uploadedFile?.id,
        companyId: uploadedFile?.company_id,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsRetryLoading(false);
      reloadQuestionnairePreview();
    }
  };

  const pillLabel = useMemo((): string => {
    switch (uploadedFile.status) {
      case 'analysis_succeeded':
        return 'Completed';
      case 'split_succeeded':
        return 'Completed';
      case 'review_required':
        return 'Processed';
      case 'identify_succeeded':
        return 'Processed';
      case 'processing':
        return 'Processing';
      case 'split_failed':
        return 'Failed';
      case 'identify_failed':
        return 'Failed';
      default:
        // Last-resort fallback to convert snake_case to Title Case and remove underscores
        return uploadedFile.status
          .split('_')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    }
  }, [uploadedFile.status]);

  const pillIntent = useMemo((): PillProps['intent'] => {
    if (['Processed', 'Completed'].includes(pillLabel)) return 'success';
    if (pillLabel === 'Processing') return 'info';

    return 'danger';
  }, [pillLabel]);

  const unrecognizedPages: Array<unknown> = useMemo(() => {
    // If the document has a detailed_name, it is already recognized and has no child documents.
    if (uploadedFile.detailed_name) {
      return [];
    }

    if (uploadedFile.page_count) {
      return Array(uploadedFile.page_count)
        .fill(null)
        .map((_, index) => {
          const page = index + 1;
          return page;
        })
        .filter((page) => {
          return !getChildDocuments(
            uploadedFile,
            questionnaire?.extracted_documents,
          ).some((childDoc: any) => {
            return childDoc.parent_pages.includes(page);
          });
        });
    } else {
      return [];
    }
  }, [uploadedFile, questionnaire]);

  const isDialogOpen: boolean =
    typeof isBUPReviewDialogOpenForSource === 'string' &&
    isBUPReviewDialogOpenForSource === uploadedFile?.source;

  return (
    <>
      {isDialogOpen && (
        <ReviewDialog
          childDocuments={documentsRecognized}
          isOpen={isDialogOpen}
          onClose={() => setIsBUPReviewDialogOpenForSource(null)}
          uploadedFile={uploadedFile}
          questionnaire={questionnaire}
          reloadQuestionnairePreview={reloadQuestionnairePreview}
        />
      )}
      <Card>
        <Flex isColumn>
          <Flex padding={6} isYCentered={true} isBetween={true}>
            <Flex className={styles.textFlex} gap={4} xAlign="start">
              <Icon name="file" size={16} />
              <Text type="div" fontWeight="medium">
                {uploadedFile.name}
              </Text>
            </Flex>
            <Flex gap={6} isYCentered={true} xAlign="end">
              <Flex isYCentered={true} gap={4}>
                <Pill
                  intent={pillIntent}
                  label={pillLabel}
                  isLoading={uploadedFile.status === 'processing'}
                  isRound={uploadedFile.status === 'processing'}
                />
                {pillLabel === 'Failed' && (
                  <ButtonGroup>
                    <Button
                      intent="secondary"
                      iconLeft="refresh"
                      withTooltip={true}
                      isLoading={isRetryLoading}
                      isDisabled={isRetryLoading}
                      tooltip="Click to try again"
                      onClick={handleRetry}
                      value="Retry"
                      size="tiny"
                      isIconOnly={true}
                    />
                  </ButtonGroup>
                )}
              </Flex>
              <Separator height={20} isVertical={true} isLight={true} />
              <ButtonGroup>
                {pillLabel === 'Processed' && (
                  <>
                    <Button
                      isDisabled={isRemovingDoc}
                      intent="primary"
                      onClick={() =>
                        setIsBUPReviewDialogOpenForSource(uploadedFile.source)
                      }
                      type="button"
                      value="Review"
                      size="small"
                      isPulsing={true}
                    />
                    <Button
                      intent="secondary"
                      size="tiny"
                      value="Redo"
                      isIconOnly={true}
                      iconLeft="refresh"
                      tooltip="Re-recognize the document"
                      withTooltip={true}
                      onClick={() => {
                        handleRetry();
                      }}
                    />
                  </>
                )}
                {pillLabel === 'Completed' && (
                  <Button
                    isDisabled={isRemovingDoc}
                    intent="secondary"
                    onClick={() =>
                      setIsBUPReviewDialogOpenForSource(uploadedFile.source)
                    }
                    type="button"
                    size="tiny"
                    value="Edit"
                  />
                )}
                <Button
                  isDisabled={isRemovingDoc}
                  intent="danger-secondary"
                  onClick={() => {
                    if (!isRemovingDoc) {
                      onRemoveDocument(uploadedFile.id!);
                    }
                  }}
                  type="button"
                  isIconOnly={true}
                  size="tiny"
                  iconLeft="trash"
                  value="Remove"
                />
              </ButtonGroup>
            </Flex>
          </Flex>
          {unrecognizedPages.length > 0 && wasSuccessful && (
            <div className="p-4">
              <Alert type="warning">
                There are {unrecognizedPages.length} page(s) that are not yet
                classified.
              </Alert>
            </div>
          )}
          {documentsRecognized.length > 0 && wasSuccessful && (
            <>
              {pillLabel === 'Processed' && (
                <Flex padding={6} isColumn={true}>
                  <Alert type="warning">
                    Review is required before the document can be split and
                    matched to items from the document checklist below.
                  </Alert>
                </Flex>
              )}
              <Separator isLight />
              <Flex gap={16} padding={6}>
                <Heading level={7}>Documents recognized:</Heading>
                <ul>
                  {documentsRecognized.map((dr) => (
                    <li key={dr.id}>
                      <Flex gap={2} isYCentered={true} xAlign="start">
                        <Icon color="secondary" name="file" size={12} />
                        <Text type="small" color="secondary">
                          {dr.detailed_name}
                        </Text>
                      </Flex>
                    </li>
                  ))}
                </ul>
              </Flex>
            </>
          )}
        </Flex>
      </Card>
    </>
  );
};
